import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Search,
    ArrowRight,
    CornerDownLeft,
    Mail,
    User,
    LayoutTemplate,
    Plus,
    Loader2,
} from "lucide-react";
import { cn } from "lib/utils";
import { useCampaigns, useCustomers, useUserTemplates } from "core";
import { getAllDestinations, type NavItem } from "new-components/layout/navigation";
import { StatusBadge } from "components/ui/status-badge";

type ResultKind = "page" | "action" | "campaign" | "lead" | "template";

interface Result {
    id: string;
    kind: ResultKind;
    label: string;
    subtitle?: string;
    icon: React.ComponentType<{ className?: string }>;
    path: string;
    /** Rendered on the right, e.g. a status pill */
    trailing?: React.ReactNode;
    keywords?: string[];
}

const GROUP_LABEL: Record<ResultKind, string> = {
    action: "Actions",
    page: "Go to",
    campaign: "Campaigns",
    lead: "Contacts",
    template: "Templates",
};

const GROUP_ORDER: ResultKind[] = ["action", "page", "campaign", "lead", "template"];

/** Quick creates — the palette's second job is starting work, not only finding it. */
const QUICK_ACTIONS: Result[] = [
    {
        id: "action-new-campaign",
        kind: "action",
        label: "Create campaign",
        subtitle: "Start a new email campaign",
        icon: Plus,
        path: "/campaigns/new",
        keywords: ["new", "create", "campaign", "add"],
    },
    {
        id: "action-new-template",
        kind: "action",
        label: "Create template",
        subtitle: "Open the template editor",
        icon: Plus,
        path: "/templates/editor/new",
        keywords: ["new", "create", "template", "design", "add"],
    },
    {
        id: "action-new-lead",
        kind: "action",
        label: "Add contact",
        subtitle: "Create a new lead",
        icon: Plus,
        path: "/leads/new",
        keywords: ["new", "create", "lead", "contact", "add", "person"],
    },
];

function matches(query: string, ...fields: Array<string | undefined | string[]>): boolean {
    const q = query.toLowerCase();
    return fields.some((field) => {
        if (!field) return false;
        if (Array.isArray(field)) return field.some((f) => f.toLowerCase().includes(q));
        return field.toLowerCase().includes(q);
    });
}

interface CommandPaletteProps {
    open: boolean;
    onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const restoreFocusTo = useRef<HTMLElement | null>(null);
    const navigate = useNavigate();

    const trimmed = query.trim();
    // Only reach for records once the query is specific enough to be useful.
    const searchingData = open && trimmed.length >= 2;

    const { data: campaignData, isFetching: campaignsFetching } = useCampaigns(
        { page: 0 },
        { enabled: searchingData }
    );
    const { data: leadData, isFetching: leadsFetching } = useCustomers(
        { page: 1, pageSize: 25, search: trimmed },
        { enabled: searchingData }
    );
    const { data: templateData, isFetching: templatesFetching } = useUserTemplates(
        { paginator: 0 },
        { enabled: searchingData }
    );

    const isFetching = searchingData && (campaignsFetching || leadsFetching || templatesFetching);

    const destinations = useMemo<Result[]>(
        () =>
            getAllDestinations().map((item: NavItem) => ({
                id: `page-${item.id}`,
                kind: "page" as const,
                label: item.label,
                subtitle: item.description,
                icon: item.icon,
                path: item.path,
                keywords: item.keywords,
            })),
        []
    );

    const results = useMemo<Result[]>(() => {
        if (!trimmed) {
            return [...QUICK_ACTIONS, ...destinations];
        }

        const out: Result[] = [];

        out.push(
            ...QUICK_ACTIONS.filter((a) => matches(trimmed, a.label, a.subtitle, a.keywords))
        );
        out.push(
            ...destinations.filter((d) => matches(trimmed, d.label, d.subtitle, d.keywords))
        );

        if (searchingData) {
            const campaigns = (campaignData?.items ?? [])
                .filter((c: any) => matches(trimmed, c?.name, c?.temp_name))
                .slice(0, 5)
                .map((c: any) => ({
                    id: `campaign-${c.id}`,
                    kind: "campaign" as const,
                    label: c.name ?? "Untitled campaign",
                    subtitle: c.temp_name ? `Template: ${c.temp_name}` : undefined,
                    icon: Mail,
                    path: "/campaigns",
                    trailing: c.c_status ? <StatusBadge status={c.c_status} size="xs" /> : undefined,
                }));
            out.push(...campaigns);

            const leads = (leadData?.data ?? [])
                .filter((l: any) => matches(trimmed, l?.name, l?.email, l?.phoneNumber))
                .slice(0, 5)
                .map((l: any) => ({
                    id: `lead-${l.id}`,
                    kind: "lead" as const,
                    label: l.name || l.email || "Unnamed contact",
                    subtitle: l.email ?? undefined,
                    icon: User,
                    path: `/leads`,
                }));
            out.push(...leads);

            const templates = (templateData?.items ?? [])
                .filter((t: any) => matches(trimmed, t?.name, t?.tag, t?.description))
                .slice(0, 5)
                .map((t: any) => ({
                    id: `template-${t.id}`,
                    kind: "template" as const,
                    label: t.name ?? "Untitled template",
                    subtitle: t.tag || t.description || undefined,
                    icon: LayoutTemplate,
                    path: `/templates/editor/${t.id}`,
                }));
            out.push(...templates);
        }

        return out;
    }, [trimmed, destinations, searchingData, campaignData, leadData, templateData]);

    /** Flat list is what the keyboard walks; groups are only for rendering. */
    const grouped = useMemo(() => {
        const byKind = new Map<ResultKind, Result[]>();
        for (const result of results) {
            const bucket = byKind.get(result.kind) ?? [];
            bucket.push(result);
            byKind.set(result.kind, bucket);
        }
        return GROUP_ORDER.filter((kind) => byKind.get(kind)?.length).map((kind) => ({
            kind,
            label: GROUP_LABEL[kind],
            items: byKind.get(kind)!,
        }));
    }, [results]);

    const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

    useEffect(() => {
        if (!open) return;
        restoreFocusTo.current = document.activeElement as HTMLElement;
        setQuery("");
        setSelectedIndex(0);
        const id = setTimeout(() => inputRef.current?.focus(), 40);
        return () => clearTimeout(id);
    }, [open]);

    useEffect(() => {
        if (!open) restoreFocusTo.current?.focus?.();
    }, [open]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [flat.length]);

    const go = useCallback(
        (path: string) => {
            navigate(path);
            onClose();
        },
        [navigate, onClose]
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex((i) => (flat.length ? (i + 1) % flat.length : 0));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex((i) => (flat.length ? (i - 1 + flat.length) % flat.length : 0));
            } else if (e.key === "Enter" && flat[selectedIndex]) {
                e.preventDefault();
                go(flat[selectedIndex].path);
            } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
        },
        [flat, selectedIndex, go, onClose]
    );

    useEffect(() => {
        if (!listRef.current) return;
        const el = listRef.current.querySelector<HTMLElement>('[data-selected="true"]');
        el?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex, flat.length]);

    if (!open) return null;

    let cursor = -1;

    return (
        <>
            <div
                className="fixed inset-0 z-[120] bg-[color:var(--overlay)] backdrop-blur-[2px] animate-overlay-in"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label="Search and commands"
                className="fixed inset-x-0 top-[10%] z-[121] mx-auto w-full max-w-xl px-4 animate-slide-in-up"
            >
                <div className="bg-surface-elevated rounded-2xl shadow-overlay border border-line overflow-hidden">
                    {/* ── Query ── */}
                    <div className="flex items-center gap-3 px-4 h-14 border-b border-line">
                        {isFetching ? (
                            <Loader2 className="w-4 h-4 text-brand animate-spin flex-shrink-0" aria-hidden="true" />
                        ) : (
                            <Search className="w-4 h-4 text-content-subtle flex-shrink-0" aria-hidden="true" />
                        )}
                        <input
                            ref={inputRef}
                            type="text"
                            role="combobox"
                            aria-expanded="true"
                            aria-controls="command-results"
                            aria-activedescendant={flat[selectedIndex]?.id}
                            aria-autocomplete="list"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search campaigns, contacts, templates or pages…"
                            className="flex-1 bg-transparent text-ui-lg text-content placeholder:text-content-subtle outline-none border-none"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        <kbd className="hidden sm:inline-flex items-center px-1.5 h-5 rounded border border-line bg-surface-sunken text-2xs font-sans font-semibold text-content-subtle">
                            ESC
                        </kbd>
                    </div>

                    {/* ── Results ── */}
                    <div
                        id="command-results"
                        ref={listRef}
                        role="listbox"
                        aria-label="Results"
                        className="max-h-[min(26rem,60dvh)] overflow-y-auto overscroll-contain py-2"
                    >
                        {flat.length === 0 ? (
                            <div className="px-4 py-10 text-center">
                                <p className="text-ui text-content-muted">
                                    No matches for “{trimmed}”
                                </p>
                                <p className="text-meta text-content-subtle mt-1">
                                    Try a campaign name, a contact's email, or a page.
                                </p>
                            </div>
                        ) : (
                            grouped.map((group) => (
                                <div key={group.kind} className="mb-1 last:mb-0">
                                    <p className="px-4 pt-2 pb-1 text-2xs font-bold uppercase tracking-wider text-content-subtle">
                                        {group.label}
                                    </p>
                                    {group.items.map((item) => {
                                        cursor += 1;
                                        const index = cursor;
                                        const Icon = item.icon;
                                        const isSelected = index === selectedIndex;
                                        return (
                                            <button
                                                key={item.id}
                                                id={item.id}
                                                role="option"
                                                aria-selected={isSelected}
                                                data-selected={isSelected}
                                                onClick={() => go(item.path)}
                                                onMouseMove={() => setSelectedIndex(index)}
                                                className={cn(
                                                    "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-100",
                                                    isSelected
                                                        ? "bg-brand-subtle text-brand-on-subtle"
                                                        : "text-content-secondary hover:bg-surface-hover"
                                                )}
                                            >
                                                <span
                                                    className={cn(
                                                        "flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg",
                                                        isSelected
                                                            ? "bg-brand text-brand-fg"
                                                            : "bg-surface-sunken text-content-muted"
                                                    )}
                                                    aria-hidden="true"
                                                >
                                                    <Icon className="w-4 h-4" />
                                                </span>

                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-ui font-semibold truncate">
                                                        {item.label}
                                                    </span>
                                                    {item.subtitle && (
                                                        <span className="block text-micro text-content-muted truncate mt-0.5">
                                                            {item.subtitle}
                                                        </span>
                                                    )}
                                                </span>

                                                {item.trailing}

                                                {isSelected && (
                                                    <ArrowRight
                                                        className="w-3.5 h-3.5 flex-shrink-0 opacity-70"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>

                    {/* ── Keyboard legend. The palette is a keyboard-first
                           surface; showing the keys is how it gets learned. ── */}
                    <div className="flex items-center gap-4 px-4 py-2.5 border-t border-line bg-surface-muted text-2xs text-content-subtle">
                        <span className="inline-flex items-center gap-1">
                            <kbd className="px-1 h-4 inline-flex items-center rounded border border-line bg-surface font-sans">↑</kbd>
                            <kbd className="px-1 h-4 inline-flex items-center rounded border border-line bg-surface font-sans">↓</kbd>
                            navigate
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <kbd className="px-1 h-4 inline-flex items-center rounded border border-line bg-surface font-sans">
                                <CornerDownLeft className="w-2.5 h-2.5" aria-hidden="true" />
                            </kbd>
                            open
                        </span>
                        <span className="ml-auto hidden sm:inline">
                            Searches campaigns, contacts and templates
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CommandPalette;
